import type zh from '../zh/chat';

/**
 * Frozen copy of the chat text the UI showed before the zh/en switch, rendered while
 * UI_I18N_ENABLED is off (gamma / prod). Do not edit the text; add a key here only when
 * zh gains one, with the text the UI would show without the switch. Delete with the flag.
 */
const chat: typeof zh = {
  /** First assistant message of a fresh session, per agent (AgentContext). */
  welcome: {
    rocky: '你好！👋 我是 Rocky，有什么我可以帮你的吗？',
    marketing_agent: "Hi! 👋 I'm the Marketing Agent. How can I help?",
    campaign_copilot_agent: "Hi! 👋 I'm the Campaign Copilot. How can I help?",
    eval: '你好！👋 我是 Evaluation Agent，可以帮你查看评估运行、失败案例和质量趋势。有什么我可以帮你的吗？',
  },
  header: {
    title: 'Chat',
    historyTitle: 'Chat History',
    history: 'Chat history',
    backToChat: 'Back to chat',
    newConversation: 'New conversation',
    enterFullscreen: 'Enter fullscreen',
    exitFullscreen: 'Exit fullscreen',
    close: 'Close chat',
    open: 'Open chat',
  },
  thought: {
    thinking: 'Thinking...',
    completed: 'Thinking completed',
    hint: '为了数据准确性我可能会使用更长的时间，等待时您可以点击聊天窗口上方<icon />，唤起新的对话，后台任务将持续运行',
    newChatAlt: '新对话',
  },
  /** Follow-up suggestions under an answer (choice_group heading / group name). */
  followUps: {
    title: '接下来可以',
  },
  suggestions: {
    title: 'Suggested Questions',
  },
  disclaimer: 'Rocky is AI and can make mistakes.',
  messages: {
    loadingOlder: 'Loading older messages...',
    empty: 'This conversation has no messages yet.',
    unknownError: 'Unknown error',
    userAvatar: 'Avatar of {{name}}',
    userFallback: 'User',
    assistantAvatar: 'Avatar of Rocky AI assistant',
    genericAssistantAvatar: 'Avatar of AI assistant',
    noResponse: '(No response)',
    stopped: '(Stopped)',
    scrollToLatest: 'Scroll to latest message',
  },
  errors: {
    auth: 'Authentication error. Please refresh the page to re-login.',
    withMessage: 'Error: {{message}}',
    noResponse: 'No response',
    sendFailed: '抱歉，发送失败，请重试。',
  },
  session: {
    switchConfirm: '当前对话正在连接中，切换可能丢失回复。确定切换吗？',
  },
  input: {
    model: 'Model',
    placeholder: 'How can I assist you today? Send a message to start.',
    readOnly: 'Read-only mode (Admin View)',
    emailFallback: '不想等了，做完直接把报告和分析发我邮箱',
    willQueue: '问题将加入待发送队列…',
    gateBlocks: '请先处理上方的审批卡片（批准 / 取消）',
    gateReply: '回复上方卡片：修改意见或问题（批准请点卡片按钮）',
    stop: 'Stop generating',
    stopKeepQueue: 'Stop current response (queued questions will continue)',
    context: 'Context: {{percent}}%',
    send: 'Send message',
    addToQueue: 'Add to pending queue',
  },
  queue: {
    gateFirst: '请先处理上方的审批卡片',
    full: '待发送队列最多 {{max}} 条',
    label: 'Pending questions queue',
    remove: 'Remove from queue',
    removeNamed: 'Remove queued question: {{text}}',
    sendNow: 'Send now (stops current response)',
    sendNowNamed: 'Send queued question now: {{text}}',
  },
  files: {
    attach: 'Attach files',
    attachLabel: 'Attach Files',
    drop: 'Drop files to upload',
    uploading: 'Uploading...',
    remove: 'Remove file',
    removeNamed: 'Remove {{name}}',
    unsupported: '暂不支持上传 {{exts}} 类型的文件，请转换为 PDF、CSV、TXT 等支持的格式后重试。',
    listSeparator: '、',
    openInWorkspace: 'Open in workspace',
  },
  contextFile: {
    banner: '正在围绕《<name>{{fileName}}</name>》对话',
    stopNamed: 'Stop using {{fileName}} as context',
    stop: 'Stop using this file as chat context',
    chipExcludeTitle: '{{name}} (click to exclude from context)',
    chipIncludeTitle: '{{name}} (click to include in context)',
    chipExclude: 'Exclude {{name}} from context',
    chipInclude: 'Include {{name}} in context',
  },
  scope: {
    kb: 'KB: {{name}}',
    assetTitle: 'Chat 已绑定到 Asset「{{name}}」，点击退出',
    assetExit: 'Exit Asset KB scope: {{name}}',
    useCaseTitle: 'Chat 已绑定到 Use Case「{{name}}」，点击退出',
    useCaseExit: 'Exit Use Case KB scope: {{name}}',
    storylineTitle: 'Chat 已绑定到 Storyline「{{name}}」，点击退出',
    storylineExit: 'Exit Storyline KB scope: {{name}}',
  },
  pack: {
    label: "Rocky's Pack",
    agentLabel: 'Skill Pack',
    open: 'Open {{label}} skills',
    unavailable: '{{label}} (unavailable while loading)',
    descriptions: {
      DataRetriever:
        'Fetches revenue, usage, pipeline, and account data instantly - no more digging through dashboards or waiting for report pulls.',
      Hedwig:
        'Intelligence briefs for field teams — fetching industrial data and resources to deliver industry signals, competitor moves, and customer insights that drive actions.',
      Cheetah:
        'Cheetah provides internal expert booking service — match the right person to your needs, one-click booking and track the entire engagement workflow.',
      IVT: 'Industry vertical solutions — explore solution maps, storylines, assets and motions to power vertical selling.',
      CampaignCopilot:
        'Campaign orchestration — audience segmentation, personalized emails, Octopus program creation and performance analytics.',
      EventInsights: 'Event insights — pre- and post-event data reports.',
    },
  },
  markdown: {
    code: 'Code',
    copy: 'Copy',
    copied: 'Copied',
  },
  history: {
    loading: 'Loading sessions...',
    empty: 'No chat history yet',
    untitled: 'Untitled conversation',
    loadMore: 'Load more',
    loadingMore: 'Loading...',
    loadFailed: 'Failed to load sessions',
    loadMoreFailed: 'Failed to load more sessions',
    loadConversationFailed: 'Failed to load conversation',
    justNow: 'just now',
    minutesAgo: '{{n}}m ago',
    hoursAgo: '{{n}}h ago',
    daysAgo: '{{n}}d ago',
  },
};

export default chat;
